import { useLocation } from "@reach/router";
import { LocalizationMap, Viewer, Worker } from "@react-pdf-viewer/core";
import es_ES from "@react-pdf-viewer/locales/lib/es_ES.json";
import { Link, navigate } from "gatsby";
import * as React from "react";
import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CONTENT_TYPE } from "../../../../company/entities";
import { ILink, Maybe } from "../../types/thecookies";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import { SingleRow } from "../ColumnArea/column-gql";
import Markdown from "../Markdown/Markdown";
import NestedContentList from "../NestedContentList/NestedContentList";
import { is_containsProtocols } from "../shared/utils/getLink";
import { getSlugWithLang } from "../shared/utils/getSlugWithLang";
import Tabs, { ExtendedSanityAssetOrAssetFile } from "../Tabs/Tabs";
import { close_svg } from "./placeholder-elements";

type PropsPlaceholder = PropsWithChildren<{
  data: Queries.SanityContent &
    Maybe<{ name: string; list?: Queries.SanityPost[] }> &
    Maybe<{ pdfFile: { asset: { url: string } } }>;
  settings?: { isModal?: boolean; breadcrumbs?: ILink[]; nestedContent?: Maybe<SingleRow[]> };
}>;

const Placeholder: React.FC<PropsPlaceholder> = (props: PropsPlaceholder) => {
  const { t } = useTranslation("global");
  const placeholder = useRef() as React.MutableRefObject<HTMLDivElement>;
  const [is_loadContent, setIsLoadContent] = useState<boolean>(false);
  if (!props.data && props.children && !props.settings?.isModal)
    return <div className={`m-placeholder`}>{props.children}</div>;

  function setSizePlaceholder() {
    if (!placeholder?.current) return;
    const navbar = document.getElementsByClassName("placeholder__navbar")[0] as HTMLElement;
    const navbarVerticalSpace = navbar?.scrollHeight || 36;
    const paddingTopPlaceholder = parseInt(getComputedStyle(placeholder.current).paddingTop.replace("px", "")) * 2;
    const heightToPlaceholder = placeholder.current.scrollHeight - navbarVerticalSpace - paddingTopPlaceholder - 1;
    placeholder.current.style.setProperty("--height-placeholder-body", `${heightToPlaceholder}px`);
  }

  useEffect(() => {
    if (props.data?.redirect && !is_containsProtocols(props.data.redirect) && window) {
      navigate(props.data.redirect);
    }
    setSizePlaceholder();
    window.addEventListener("resize", setSizePlaceholder);

    return () => {
      window.removeEventListener("resize", setSizePlaceholder);
    };
  }, []);

  const { pathname } = useLocation();
  const contentSpecial = /\/espacio-\d+/.test(pathname);
  const containerClass = contentSpecial ? "placeholder__content--text-special" : "placeholder__content--text";


  const isTypeContentBlog =
    props.data?.contentType === CONTENT_TYPE.BLOG || props.data?.contentType === CONTENT_TYPE.POST;

  if (props.children && !isTypeContentBlog)
    return (
      <>
        <div className={`m-placeholder__overlay`} onClick={() => navigate("/")} />
        <div ref={placeholder} className={`m-placeholder ${props.settings?.isModal ? "m-placeholder--modal" : ""}`}>
          <div className={"placeholder__navbar"}>
            {props.settings?.breadcrumbs ? <Breadcrumbs data={props.settings.breadcrumbs} /> : <div />}
            <button
              onClick={() => {
                navigate("/");
              }}
              className={"button-clean"}
            >
              {close_svg}
            </button>
          </div>
          <main className={"placeholder__content"}>{props.children}</main>
        </div>
      </>
    );

  return (
    <>
      {props.settings?.isModal && <div className={`m-placeholder__overlay`} onClick={() => navigate("/")} />}
      <div ref={placeholder} className={`m-placeholder ${props.settings?.isModal ? "m-placeholder--modal" : ""}`}>
        {props.settings?.isModal && (
          <div className={"placeholder__navbar"}>
            {props.settings?.breadcrumbs ? <Breadcrumbs data={props.settings.breadcrumbs} /> : <div />}
            <button
              onClick={() => {
                navigate("/");
              }}
              className={"button-clean"}
            >
              {close_svg}
            </button>
          </div>
        )}
        <div className="placeholder__body">
          {props.settings?.nestedContent && props.settings?.breadcrumbs ? (
            <NestedContentList data={props.settings.nestedContent} breadcrumbs={props.settings.breadcrumbs} />
          ) : (
            <aside />
          )}
          <main className={"placeholder__content"}>
            {props.data?.contentType === CONTENT_TYPE.POST && (
              <>
                <h1>{props?.data?.name}</h1>
                {props.data.text && <Markdown markdown={props.data.text} />}
                {props.data.text && props.data?.pdfFile && <hr />}
                {props.data?.pdfFile && (
                  // <object
                  //   id={"object"}
                  //   className={"placeholder__pdf"}
                  //   data={`${props.data?.pdfFile.asset.url}`}
                  //   type="application/pdf"
                  // >
                  <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.js">
                    <div className={"placeholder__pdf"}>
                      <Viewer
                        localization={es_ES as unknown as LocalizationMap}
                        fileUrl={props.data?.pdfFile.asset.url || "/LitografiasEDA.pdf"}
                        // plugins={[
                        //   defaultLayoutPluginInstance,
                        // ]}
                      />
                    </div>
                  </Worker>
                )}
              </>
            )}
            {props.data?.contentType === CONTENT_TYPE.BLOG && (
              <>
                <h1 className={"title__blog"}>{CONTENT_TYPE.BLOG}</h1>
                <div className="placeholder__content--blog">
                  <ul className="placeholder__content--blog-list">
                    {props.data.list?.map((post) => (
                      <li key={post.id}>
                        <Link
                          className={"column__item--red"}
                          to={getSlugWithLang(`/${CONTENT_TYPE.BLOG}/${post.slug}`, null)}
                        >
                          {post.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
            <div className={`placeholder__content--text ${containerClass}`}>
              {props.data.text && props.data?.contentType !== CONTENT_TYPE.POST && (
                <Markdown markdown={props.data.text} />
              )}
            </div>
            <Tabs data={{ assets: props.data?.assets as unknown as ExtendedSanityAssetOrAssetFile[] }} />
            {props.data?.redirect &&
              (is_containsProtocols(props.data.redirect) ? (
                <iframe className={"--cover"} src={`${props.data.redirect}`} width="100%" height={"100%"} />
              ) : (
                <iframe className={"--cover"} src={`https://${props.data.redirect}`} width="100%" height={"100%"} />
              ))}
          </main>
        </div>
      </div>
    </>
  );
};

export default Placeholder;
